import { useEffect, useRef } from 'react';
import { LocalNotifications } from '@capacitor/local-notifications';
import { isNativeApp, syncNativeAlarms, silenceStartedAlarms, handleAlarmAction } from '../utils/nativeAlarms';

// How often it's worth re-handing the reminders to Android. The sync is one
// small request, but there's no point repeating it on every screen change.
//
// It was five minutes, and five minutes is a long time to be wrong about an
// alarm. An event added on the laptop, or on the phone by someone else in a
// shared calendar, could sit unknown to Android for that whole window — and if
// it was due inside it, it simply never rang. Sixty seconds still stops the
// sync firing on every flick between screens, and it is short enough that
// "I added it and it didn't ring" stops being a thing that can happen.
const MIN_GAP_MS = 60 * 1000;

/**
 * Keeps Android's own reminder queue in step with the user's events.
 *
 * Runs ONLY inside the installed app (see nativeAlarms.js for why the web
 * doesn't need it). Syncs when the app opens and every time it comes back to
 * the foreground — that second one matters: it's how an event added on the
 * laptop reaches the phone's alarm queue.
 */
export function useNativeAlarms(user) {
  const lastRef = useRef(0);

  useEffect(() => {
    if (!user || !isNativeApp()) return;
    const lang = user.language || 'it';

    const run = (force = false) => {
      // Whoever is looking at MeMo has answered the call: stop the ringing
      // FIRST and always, never behind the sync's throttle.
      silenceStartedAlarms().catch(() => {});
      const now = Date.now();
      if (!force && now - lastRef.current < MIN_GAP_MS) return;
      lastRef.current = now;
      syncNativeAlarms(lang).catch(() => {});
    };

    run(true);
    const onVisible = () => { if (document.visibilityState === 'visible') run(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);

    // The Stop / +5 min buttons on the notification. Android delivers the tap
    // by starting the app — that's how the notification component builds those
    // buttons, and it's why they can't work with MeMo fully closed without
    // replacing the whole notification machinery with hand-written Android
    // code. From the user's side it's still one tap on the lock screen.
    let handle;
    LocalNotifications.addListener('localNotificationActionPerformed', (e) => {
      handleAlarmAction(e?.actionId, e?.notification).catch(() => {});
    }).then((h) => { handle = h; }).catch(() => {});

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
      try { handle?.remove?.(); } catch (_) {}
    };
  }, [user]);
}
